import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Checkbox, Alert, Snackbar, Tooltip, FormControlLabel, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, CheckCircle as ApproveIcon,
  Cancel as RejectIcon, FilterList as FilterIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  fetchLeaveRequests, approveLeaveRequest, rejectLeaveRequest, cancelLeaveRequest,
  bulkApproveLeaveRequests, bulkRejectLeaveRequests, fetchLeaveTypes, fetchLeavePeriods,
} from '../../store/slices/leaveSlice';
import { AppDispatch, RootState } from '../../store/store';
import { LeaveRequestDto, LeaveRequestStatusLabels, LeaveRequestStatusColors } from '../../types';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const LeaveRequests: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { leaveRequests, leaveTypes, leavePeriods, loading } = useSelector((state: RootState) => state.leave);
  const { user } = useSelector((state: RootState) => state.auth);
  const permissions = user?.permissions || [];
  const canApprove = permissions.includes('ApproveLeave');

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [detailDialog, setDetailDialog] = useState<LeaveRequestDto | null>(null);
  const [remarksDialog, setRemarksDialog] = useState<{ open: boolean; action: 'approve' | 'reject'; id: string }>({ open: false, action: 'approve', id: '' });
  const [adminRemarks, setAdminRemarks] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchLeaveRequests({}));
    dispatch(fetchLeaveTypes());
    dispatch(fetchLeavePeriods());
  }, [dispatch]);

  const handleSearch = () => {
    dispatch(fetchLeaveRequests({
      searchTerm: searchTerm || undefined,
      status: statusFilter ? Number(statusFilter) : undefined,
      leaveTypeId: leaveTypeFilter || undefined,
    }));
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, leaveTypeFilter]);

  useEffect(() => {
    setPage(0);
  }, [leaveRequests.length]);

  const paginatedLeaveRequests = leaveRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const currentPagePendingIds = paginatedLeaveRequests.filter((request) => request.status === 0).map((request) => request.id);

  const handleApprove = async (id: string, remarks?: string) => {
    try {
      await dispatch(approveLeaveRequest({ id, data: { adminRemarks: remarks, approvedById: user?.userId || '' } })).unwrap();
      setSnackbar({ open: true, message: 'Leave request approved', severity: 'success' });
      dispatch(fetchLeaveRequests({}));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Approval failed', severity: 'error' });
    }
  };

  const handleReject = async (id: string, remarks?: string) => {
    try {
      await dispatch(rejectLeaveRequest({ id, data: { adminRemarks: remarks, rejectedById: user?.userId || '' } })).unwrap();
      setSnackbar({ open: true, message: 'Leave request rejected', severity: 'success' });
      dispatch(fetchLeaveRequests({}));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Rejection failed', severity: 'error' });
    }
  };

  const handleCancel = async (id: string) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await dispatch(cancelLeaveRequest(id)).unwrap();
      setSnackbar({ open: true, message: 'Leave request cancelled', severity: 'success' });
      dispatch(fetchLeaveRequests({}));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Cancellation failed', severity: 'error' });
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject') => {
    if (selected.length === 0) return;
    try {
      if (action === 'approve') {
        await dispatch(bulkApproveLeaveRequests({ ids: selected, approvedById: user?.userId || '' })).unwrap();
        setSnackbar({ open: true, message: `${selected.length} requests approved`, severity: 'success' });
      } else {
        await dispatch(bulkRejectLeaveRequests({ ids: selected, rejectedById: user?.userId || '' })).unwrap();
        setSnackbar({ open: true, message: `${selected.length} requests rejected`, severity: 'success' });
      }
      setSelected([]);
      dispatch(fetchLeaveRequests({}));
    } catch (err: any) {
      setSnackbar({ open: true, message: err || 'Bulk action failed', severity: 'error' });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelected((prev) => Array.from(new Set([...prev, ...currentPagePendingIds])));
    } else {
      setSelected((prev) => prev.filter((id) => !currentPagePendingIds.includes(id)));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const handleOpenRemarks = (action: 'approve' | 'reject', id: string) => {
    setAdminRemarks('');
    setRemarksDialog({ open: true, action, id });
  };

  const handleSubmitRemarks = () => {
    if (remarksDialog.action === 'approve') {
      handleApprove(remarksDialog.id, adminRemarks);
    } else {
      handleReject(remarksDialog.id, adminRemarks);
    }
    setRemarksDialog({ open: false, action: 'approve', id: '' });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>Leave Requests</Typography>
        {canApprove && selected.length > 0 && (
          <Box display="flex" gap={1}>
            <Button variant="contained" color="success" size="small" onClick={() => handleBulkAction('approve')}>
              Approve ({selected.length})
            </Button>
            <Button variant="contained" color="error" size="small" onClick={() => handleBulkAction('reject')}>
              Reject ({selected.length})
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField size="small" placeholder="Search employee..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            onKeyUp={(e) => {
              if (e.key !== 'Enter') handleSearch();
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small"><SearchIcon /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="0">Pending</MenuItem>
              <MenuItem value="1">Approved</MenuItem>
              <MenuItem value="2">Rejected</MenuItem>
              <MenuItem value="3">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Leave Type</InputLabel>
            <Select value={leaveTypeFilter} label="Leave Type" onChange={(e) => setLeaveTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {leaveTypes.map(lt => (
                <MenuItem key={lt.id} value={lt.id}>{lt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Total Records: {leaveRequests.length}
        </Typography>
        <TableContainer component={Paper}>
          <>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {canApprove && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={currentPagePendingIds.length > 0 && currentPagePendingIds.every((id) => selected.includes(id))}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                )}
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Department</strong></TableCell>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell><strong>From</strong></TableCell>
                <TableCell><strong>To</strong></TableCell>
                <TableCell><strong>Days</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Applied</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaveRequests.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center">No leave requests found</TableCell></TableRow>
              ) : (
                paginatedLeaveRequests.map((req) => (
                  <TableRow key={req.id} hover>
                    {canApprove && (
                      <TableCell padding="checkbox">
                        {req.status === 0 && (
                          <Checkbox checked={selected.includes(req.id)} onChange={() => handleSelectOne(req.id)} />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{req.employeeName}</Typography>
                      <Typography variant="caption" color="text.secondary">{req.employeeCode}</Typography>
                    </TableCell>
                    <TableCell>{req.departmentName || '-'}</TableCell>
                    <TableCell>
                      <Chip label={req.leaveTypeName} size="small"
                        sx={{ backgroundColor: req.leaveTypeColor || '#e0e0e0', color: '#fff', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>{new Date(req.startDate).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(req.endDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {req.numberOfDays} {req.isHalfDay && <Chip label={req.halfDayType || 'Half'} size="small" variant="outlined" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Chip label={LeaveRequestStatusLabels[req.status]} color={LeaveRequestStatusColors[req.status]} size="small" />
                    </TableCell>
                    <TableCell>{new Date(req.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => setDetailDialog(req)}><ViewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        {canApprove && req.status === 0 && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton size="small" color="success" onClick={() => handleOpenRemarks('approve', req.id)}>
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton size="small" color="error" onClick={() => handleOpenRemarks('reject', req.id)}>
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {req.status === 0 && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" color="warning" onClick={() => handleCancel(req.id)}>
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={leaveRequests.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
          </>
        </TableContainer>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Leave Request Details</DialogTitle>
        <DialogContent dividers>
          {detailDialog && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Employee</Typography>
                <Typography variant="body2" fontWeight={600}>{detailDialog.employeeName} ({detailDialog.employeeCode})</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Department</Typography>
                <Typography variant="body2">{detailDialog.departmentName || '-'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Leave Type</Typography>
                <Chip label={detailDialog.leaveTypeName} size="small" sx={{ backgroundColor: detailDialog.leaveTypeColor || '#e0e0e0', color: '#fff' }} />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Period</Typography>
                <Typography variant="body2">{new Date(detailDialog.startDate).toLocaleDateString()} - {new Date(detailDialog.endDate).toLocaleDateString()}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Days</Typography>
                <Typography variant="body2">{detailDialog.numberOfDays} {detailDialog.isHalfDay ? `(${detailDialog.halfDayType})` : ''}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Reason</Typography>
                <Typography variant="body2" sx={{ maxWidth: '60%', textAlign: 'right' }}>{detailDialog.reason}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip label={LeaveRequestStatusLabels[detailDialog.status]} color={LeaveRequestStatusColors[detailDialog.status]} size="small" />
              </Box>
              {detailDialog.adminRemarks && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Remarks</Typography>
                  <Typography variant="body2">{detailDialog.adminRemarks}</Typography>
                </Box>
              )}
              {detailDialog.approvedByName && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Actioned By</Typography>
                  <Typography variant="body2">{detailDialog.approvedByName} on {new Date(detailDialog.approvedAt!).toLocaleDateString()}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Remarks Dialog */}
      <Dialog open={remarksDialog.open} onClose={() => setRemarksDialog({ ...remarksDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{remarksDialog.action === 'approve' ? 'Approve' : 'Reject'} Leave Request</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Admin Remarks (optional)" value={adminRemarks}
            onChange={(e) => setAdminRemarks(e.target.value)} sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksDialog({ ...remarksDialog, open: false })}>Cancel</Button>
          <Button variant="contained" color={remarksDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitRemarks}>
            {remarksDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveRequests;
